import { KIND_COLORS, labelStep, niceCeil, utcDay } from "../lib/processing";

/* The Processing page's two charts: a bar per bucket of time, stacked by type
 * of work, with an optional line for the daily limit.
 *
 * Built in HTML rather than a stretched SVG, so its text stays sharp at any
 * width. James, 2026-10-07: the first version was too short to read and had
 * no amounts, which are the point. So the plot is 220 pixels tall, the axis
 * has round steps with gridlines, every bar has its total above it, the
 * legend gives each type's total over the window, and a hidden table gives
 * screen readers every figure. A segment's title adds its own amount, which
 * is extra and never the only place a number is. `labelAt` names a bucket: a
 * UTC day by default, because the daily buckets are UTC days. */
export type Bucket = { at: string; parts: { key: string; value: number }[] };

const TICKS = 4;

export function StackedBars({ buckets, labels, cap, format, title, labelAt = utcDay }: {
  buckets: Bucket[];
  labels: Record<string, string>;
  cap?: number | null;
  format: (n: number) => string;
  title: string;
  labelAt?: (iso: string) => string;
}) {
  if (buckets.length === 0) return <p className="muted">No history yet</p>;
  const total = (b: Bucket) => b.parts.reduce((s, p) => s + p.value, 0);
  const top = niceCeil(Math.max(cap ?? 0, ...buckets.map(total)));
  const pct = (v: number) => `${Math.min(100, (v / top) * 100)}%`;
  const kinds = [...new Set(buckets.flatMap((b) => b.parts.map((p) => p.key)))];
  const byKind = Object.fromEntries(kinds.map((k) => [k, buckets.reduce(
    (s, b) => s + b.parts.filter((p) => p.key === k).reduce((t, p) => t + p.value, 0), 0)]));
  const all = buckets.reduce((s, b) => s + total(b), 0);
  const step = labelStep(buckets.length);
  const ticks = Array.from({ length: TICKS + 1 }, (_, i) => (top * i) / TICKS);
  return (
    <figure className="chart">
      <div className="chart-plot" aria-hidden="true">
        <div className="chart-yaxis">
          {ticks.map((t) => <span key={t} style={{ bottom: pct(t) }}>{format(t)}</span>)}
        </div>
        <div className="chart-area">
          {ticks.slice(1).map((t) => <div key={t} className="chart-grid" style={{ bottom: pct(t) }} />)}
          {cap ? (
            <div className="chart-cap" style={{ bottom: pct(cap) }}><span>Daily limit now {format(cap)}</span></div>
          ) : null}
          {buckets.map((b) => {
            const t = total(b);
            return (
              <div key={b.at} className="chart-col">
                <div className="chart-stack" style={{ height: pct(t) }}>
                  {b.parts.filter((p) => p.value > 0).map((p) => (
                    <div key={p.key} className="chart-seg"
                         style={{ height: `${(p.value / (t || 1)) * 100}%`, background: KIND_COLORS[p.key] ?? KIND_COLORS.other }}
                         title={`${labels[p.key] ?? p.key}, ${labelAt(b.at)}: ${format(p.value)}`} />
                  ))}
                </div>
                {t > 0 ? <span className="chart-total" style={{ bottom: pct(t) }}>{format(t)}</span> : null}
              </div>
            );
          })}
        </div>
        <div />
        <div className="chart-x">
          {buckets.map((b, i) => <span key={b.at}>{i % step === 0 ? labelAt(b.at) : ""}</span>)}
        </div>
      </div>
      <figcaption className="chart-legend">
        <span><strong>Total {format(all)}</strong></span>
        {kinds.map((k) => (
          <span key={k}><i style={{ background: KIND_COLORS[k] ?? KIND_COLORS.other }} />{labels[k] ?? k} {format(byKind[k] ?? 0)}</span>
        ))}
      </figcaption>
      <table className="sr-only">
        <caption>{title}</caption>
        <thead>
          <tr><th scope="col">When</th>{kinds.map((k) => <th key={k} scope="col">{labels[k] ?? k}</th>)}<th scope="col">Total</th></tr>
        </thead>
        <tbody>
          {buckets.map((b) => (
            <tr key={b.at}>
              <th scope="row">{labelAt(b.at)}</th>
              {kinds.map((k) => <td key={k}>{format(b.parts.find((p) => p.key === k)?.value ?? 0)}</td>)}
              <td>{format(total(b))}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </figure>
  );
}
